import axios, {AxiosError, AxiosRequestConfig, AxiosInstance} from 'axios'
import { deepCopy } from '@/utils'
import { $s } from '@/store'
import { Extender, ApiCurrentUser, IUserLogin, ApiVw } from '@/types/api'
import EventEmitter from 'wolfy87-eventemitter'
import { IVW } from '@/types/model'
import easyapi from '@/easyapi'
axios.defaults.responseType = 'json'

/* Types */
interface RpcBase {
  jsonrpc: '2.0' | '1.0'
  id: string | number | null
}

interface JRpcRequest extends RpcBase {
  method: string
  params: any
}

interface JRpcResponse<T> extends RpcBase {
  /**
   * [JSON-RPC 2.0]
   * This member is REQUIRED on error.
   * This member MUST NOT exist if there was no error triggered during invocation.
   * The value for this member MUST be an Object as defined in section 5.1.
   */
  error?: JRpcError<any>
  /**
   * [JSON-RPC 2.0]
   * This member is REQUIRED on success.
   * This member MUST NOT exist if there was an error invoking the method.
   * The value of this member is determined by the method invoked on the Server.
   */
  result?: T
}

interface JRpcError<T> {
  /**
   * [JSON-RPC 2.0] A Number that indicates the error type that occurred.
   * This MUST be an integer.
   */
  code: number
  /** [JSON-RPC 2.0] A String providing a short description of the error.
   *  The message SHOULD be limited to a concise single sentence.
   */
  message: string
  /**
   * [JSON-RPC 2.0] A Primitive or Structured value that contains additional information about the error.
   * This may be omitted.
   * The value of this member is defined by the Server (e.g. detailed error information, nested errors etc.).
   */
  data: T
}

// Use Tagged Union to let user can check if a response is success or failed from the value of ok
interface IBaseResponse {
  /** false if failed (catch) */
  ok: boolean
}

interface SuccessResponse<T> extends IBaseResponse {
  ok: true,  // Tagged Union Type
  /** When success, this is the REAL data body */
  d: T
}

interface FailedResponse extends IBaseResponse {
  ok: false,  // Tagged Union Type
  /** if failed, this must exist */
  err: {
    /** Error code send by server (RpcError.code), or -1 if Network Error occured in client browser side */
    code: number
    /** Error message send by Server (RpcError.message,; or client side generated (e.g. 'NetworkError') */
    msg: string
    /** will passed to VueI18n.t() */
    i18nMsg?: string
  }
}

export type SafeResponse<T> = SuccessResponse<T> | FailedResponse

/**
 * Always resolved Promise.
 * == WHY =======================
 * This can ensure exception safe and type safe. (Promise's catch is not type-safe in TypeScript)
 */
export type SafePromise<T> = Promise<SafeResponse<T>>

// =========================================================
// WebSocket
// =========================================================
// UNUSED
interface PendingResolvers<T> {
  resolver: (data: T) => any
  rejecter: (data: T) => any
}
/**
 * Server actively notify client via WebSocket. Not JSON RPC.
 */
export type ws_notify_t = 'extenderChanged' | 'vwChanged'
export interface BaseWsNotify<T> {
  type: ws_notify_t
  content: T
}

export interface WsNotify_Extender extends BaseWsNotify<Extender> { type: 'extenderChanged' }
export interface WsNotify_VW extends BaseWsNotify<ApiVw> { type: 'vwChanged' }

export type WsNotify = WsNotify_Extender | WsNotify_VW

function isWsNotify (x: JRpcResponse<any> | BaseWsNotify<any>): x is BaseWsNotify<any> {
  return (x as JRpcResponse<any>).jsonrpc === undefined
}


/* RPC */
export default class RequestManager extends EventEmitter {
  public currentUser: ApiCurrentUser = { account: '', wsid: '' }
  /** WS requires login */
  public ws: WebSocket | null = null
  /** Use WebSocket to call JSONRPC, so use a Mac to record the requests & its request id from client  */
  private pendingResolvers: Map<string, (d: SafeResponse<any>) => any> = new Map()
  /** JSON RPC request `id` */
  private curId: number = 0
  private deferredWsReqCbList: Array<() => any> = []
  private _debounceTimeout: number = -1
  private http: AxiosInstance = axios.create({
    baseURL: '/api/',
    timeout: 1000,
    headers: {
      'Cache-Control': 'no-cache'
    },
  })
  public constructor () {
    super()
    this.initialize()
  }
  initialize () {
    this.getCurrentUser().then((user) => {
      if (!user) {
        if (!$s.$state.common.networkError) { $s.common.mutations.assignNetworkError(true) }
      } else {
        if (user.wsid === '') {
          this.emit('loggedOut')
        } else {
          this.establishWs(user.wsid)
        }
      }
    })
  }
  establishWs (wsid: string) {
    if (!wsid) {
      console.warn('[To Developer] WS is able to be established after login.')
    }
    /** WebSocket */
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) {
        console.warn('[To Developer] WS has already opened. abort.')
        return
      }
    }
    const wsPrefix = window.location.origin.replace(/http/, 'ws')
    const wsUrl: string = `${wsPrefix}/ws/${wsid}`

    const WS_HANDLER = (ev: MessageEvent): void => {
      clearTimeout(this._debounceTimeout)
      const received: JRpcResponse<any> | WsNotify = JSON.parse(ev.data)
      if (!isWsNotify(received)) {
        // Condition 1 : JSON RPC
        console.log('ws.onmessage', received)
        const resolver = this.pendingResolvers.get(received.id + '')
        if (resolver) {
          if (received.error) {
            resolver({
              ok: false,
              err: {
                code: received.error.code,
                msg: received.error.message
              }
            })
          } else {
            resolver({ ok: true, d: received.result })
          }
        }
      } else {
        // Condition 2: WsNotify
        switch (received.type) {
          case 'extenderChanged':
          $s.device.mutations.assignDevFromWs(received.content)
          break

        }
      }
    }
    this.ws = new WebSocket(wsUrl)
    this.ws.onopen = () => {
      $s.common.mutations.assignNetworkError(false)
      this.emit('wsOpened')
      this.deferredWsReqCbList.forEach((cb) => { cb() })
      easyapi.fetchAllFirstTimeData()
    }
    this.ws.onerror = (err) => {
      console.log('[ws][ERROR] ', err)
    }
    this.ws.onmessage = WS_HANDLER
    this.ws.onclose = (ev) => {
      console.log('[ws][CLOSE]', ev)
      if (!$s.$state.common.networkError) {
        $s.common.mutations.assignNetworkError(true)
      }
      this.tryToReEstablishWs()
    }
  }
  public ensureWsAvailable (cb: () => any) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      cb()
    } else {
      this.deferredWsReqCbList.push(cb)
    }
  }
  public tryToReEstablishWs () {
    setTimeout(() => {
      console.log('[request_manager] try to re-establish WS...')
      this.getCurrentUser().then((user) => {
        if (user) {
          if (user.wsid === '') {
            console.warn('[To Developer] Try to re-establish WS connection, but login session seems expired?')
            this.emit('loggedOut')
            return
          }
          this.establishWs(user.wsid)
        } else {
          //
          this.tryToReEstablishWs()
        }
      })
    }, 1000)
  }
  public getNextId (): string {
    this.curId += 1
    this.curId %= 65535
    return this.curId.toString()
  }
  /**
   * Returns a SafePromise<T>
   * NOTE: Due to the limit of TypeScript (maybe...), I remove the type of return value.
   */
  public rpcCall (method: string, params?: any): Promise<{}> | undefined {
    if (!this.ws) {
      console.error('[To Developer] WS is not establisged yet', method, params)
      return
    }
    if (this.ws.readyState !== WebSocket.OPEN) {
      console.error(`[To Developer] JSON RPC calling when WebSocket hasn't established yet`,  method, params)
      return
    }
    const reqId = this.getNextId()
    const obj: JRpcRequest = {
      jsonrpc: '2.0',
      method,
      params,
      id: reqId,
    }
    this.ws.send(JSON.stringify(obj))
    return Promise.race([
      new Promise ((resolve, reject) => {
        setTimeout(() => {
          this.pendingResolvers.delete(reqId)
          return resolve({
            ok: false,
            err: {
              code: -1,
              msg: 'ClientNetworkError(maybe?)'
            }
          })
        }, 10000)
      }),
      new Promise ((resolve, reject) => {
        this.pendingResolvers.set(reqId, resolve)
      })
    ])
  }
  /** Just an ordinary GET request, NOT related to JSON RPC standard. */
  public get (url: string, options?: AxiosRequestConfig): Promise<any> {
    // TODO: make this always resolved?
    return new Promise((resolve, reject) => {
      return this.http.get(url, options).then((res) => {
        return resolve(res.data)
      }).catch((err: AxiosError) => {
        return reject(err as AxiosError)
      })
    })
  }
  /** Just an ordinary POST request, NOT related to JSON RPC standard. */
  public post (url: string, args?: any, config?: AxiosRequestConfig): Promise<any> {
    // TODO: make this always resolved?
    return new Promise((resolve, reject) => {
      return this.http.post(url, args, config).then((res) => {
        return resolve(res.data)
      }).catch((err: AxiosError) => {
        return reject(err)
      })
    })
  }
  public postAsForm (url: string, args: object, config?: AxiosRequestConfig): Promise<any> {
    // TODO: make this always resolved?
    const params = new URLSearchParams()
    // if (Object.keys(args).length === 0) { return Promise.reject() }
    for (const k in args) {
      const v = args[k] + ''
      params.append(k, v)
    }
    return new Promise((resolve, reject) => {
      return this.http.post(url, params, config).then((res) => {
        return resolve(res.data)
      }).catch((err: AxiosError) => {
        return reject(err)
      })
    })
  }
  /** undefined means NetworkError. */
  public getCurrentUser (): Promise<ApiCurrentUser|undefined> {
    console.log('get current user.....')
    return new Promise((resolve, reject) => {
      this.get('/curruser').then((user: ApiCurrentUser | undefined) => {
        console.log('got user => ', user)
        if (user) {
          console.log('!!user', user)
          // get response from server (server must always a )
          if (this.currentUser.wsid !== '') {  // if has no logged-in session available
            this.establishWs(user.wsid)
            this.currentUser = user
          }
          this.emit('currentUserGotten', user)
          resolve(user)
        } else {
          // network server
          this.emit('loggedOut')
          this.emit('currentUserGotten', null)
          return resolve(undefined)
        }
      }).catch((err) => {
        this.emit('currentUserGotten', null)
        console.error('[Error] Error when GET /curruser: axios =>', err)
        return resolve(undefined)
      })
    })
  }
  public login (form: IUserLogin): Promise<ApiCurrentUser> {
    return this.post('/login', form).then((res: ApiCurrentUser) => {
      console.log('[Login]', res)
      const needWs = this.currentUser.account === ''
      this.currentUser.account = form.account
      if (needWs) {
        this.establishWs(res.wsid)
      }
      this.emit('loggedIn', this.currentUser)
      this.emit('currentUserGotten', this.currentUser)
      return res
    }).catch((err) => {
      // FIXME: Change the return value of class RPC?
      console.warn('[Login] Failed when login =>', err.response.data.message)
      return err.response.data
    })
  }
  public logout (): Promise<any> {
    return this.post('/logout').then((res) => {
      this.currentUser.account = ''
      this.currentUser.wsid = ''
      if (this.ws) { this.ws.close() }
      this.emit('loggedOut')
      return res
    }).catch((err) => {
      console.warn('[Logout] failed:', err)
      return err
    })
  }
}
